import { useState, useCallback, useMemo } from "react";
import { ShoppingCart, RefreshCcw, Clock } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import {
  KPICard,
  ExportButton,
  DateRangePicker,
  EmptyState,
  type DateRange,
} from "@/components/admin/shared";
import { useAbandonedCarts, useAbandonedCartsSummary } from "@/hooks/admin/useCRM";
import { formatFCFA } from "@cieda/utils";
import { startOfMonth, endOfMonth, format } from "date-fns";
import type { AbandonedCartsListParams, CartStatus } from "@/lib/admin-api/crm";

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<CartStatus, string> = {
  abandoned: "Abandonné",
  recovered: "Récupéré",
  expired: "Expiré",
};

const STATUS_STYLES: Record<CartStatus, string> = {
  abandoned: "bg-orange-50 text-orange-700",
  recovered: "bg-emerald-50 text-emerald-700",
  expired: "bg-stone-100 text-stone-500",
};

// ---------------------------------------------------------------------------
// Export columns
// ---------------------------------------------------------------------------

const EXPORT_COLUMNS = [
  { key: "customer_name", label: "Client" },
  { key: "item_count", label: "Articles" },
  { key: "total_value_fcfa", label: "Valeur (FCFA)" },
  { key: "abandoned_at", label: "Abandonné le" },
  { key: "status", label: "Statut" },
] as const;

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AbandonedCarts() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  });
  const [statusFilter, setStatusFilter] = useState<CartStatus | null>(null);
  const [page, setPage] = useState(1);

  const params: AbandonedCartsListParams = useMemo(
    () => ({
      page,
      limit: 20,
      status: statusFilter,
      date_from: format(dateRange.from, "yyyy-MM-dd"),
      date_to: format(dateRange.to, "yyyy-MM-dd"),
    }),
    [page, statusFilter, dateRange],
  );

  const { data, isLoading } = useAbandonedCarts(params);
  const { data: summary, isLoading: loadingSummary } = useAbandonedCartsSummary();

  const handleDateChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setPage(1);
  }, []);

  const carts = data?.carts ?? [];
  const pagination = data?.pagination;

  const exportData = useMemo(
    () =>
      carts.map((c) => ({
        customer_name: c.customer_name,
        item_count: c.item_count,
        total_value_fcfa: c.total_value_fcfa,
        abandoned_at: new Date(c.abandoned_at).toLocaleDateString("fr-SN"),
        status: STATUS_LABELS[c.status],
      })),
    [carts],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-700">
              <ShoppingCart className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Paniers abandonnés
              </h1>
              <p className="text-sm text-stone-500">
                Suivi et récupération des paniers abandonnés
              </p>
            </div>
          </div>
          <ExportButton
            data={exportData}
            columns={[...EXPORT_COLUMNS]}
            filename="paniers-abandonnes"
          />
        </div>

        {/* KPI cards */}
        {loadingSummary ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 animate-pulse">
            {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-lg bg-stone-100" />)}
          </div>
        ) : summary ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <KPICard
              label="Paniers abandonnés"
              value={summary.total_abandoned}
              format="number"
              icon={<ShoppingCart className="h-4 w-4" />}
            />
            <KPICard
              label="Récupérés"
              value={summary.total_recovered}
              format="number"
              icon={<RefreshCcw className="h-4 w-4" />}
            />
            <KPICard
              label="Taux de récupération"
              value={summary.recovery_rate_percent}
              format="percent"
            />
            <KPICard
              label="Revenu récupéré"
              value={summary.recovered_revenue_fcfa}
              format="currency"
            />
          </div>
        ) : null}

        {/* Recovery sequence info */}
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-stone-400" />
            Séquence de rappel automatique
          </h3>
          <div className="flex flex-wrap gap-3">
            {[
              { delay: "1h", label: "Premier rappel après 1 heure" },
              { delay: "24h", label: "Deuxième rappel après 24 heures" },
              { delay: "72h", label: "Dernier rappel après 72 heures" },
            ].map((step) => (
              <div key={step.delay} className="flex items-center gap-2 rounded-lg bg-stone-50 px-3 py-2 text-sm">
                <span className="font-semibold text-amber-700">{step.delay}</span>
                <span className="text-stone-500">{step.label}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-stone-400">
            Les délais sont configurables dans les Paramètres → Automatisations.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <DateRangePicker value={dateRange} onChange={handleDateChange} />
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setStatusFilter(null); setPage(1); }}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                statusFilter === null
                  ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                  : "bg-stone-100 text-stone-600 hover:bg-stone-200"
              }`}
            >
              Tous
            </button>
            {(["abandoned", "recovered", "expired"] as CartStatus[]).map((s) => (
              <button
                key={s}
                onClick={() => { setStatusFilter(statusFilter === s ? null : s); setPage(1); }}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                    : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                }`}
              >
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
          {isLoading ? (
            <div className="space-y-3 p-4 animate-pulse">
              {[...Array(6)].map((_, i) => <div key={i} className="h-12 rounded bg-stone-100" />)}
            </div>
          ) : carts.length === 0 ? (
            <EmptyState
              icon={<ShoppingCart className="h-6 w-6" />}
              title="Aucun panier abandonné"
              description="Aucun panier ne correspond aux filtres sélectionnés."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Client</th>
                    <th className="hidden sm:table-cell px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Articles</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">Valeur</th>
                    <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Abandonné le</th>
                    <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Rappel envoyé</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">Statut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {carts.map((c) => (
                    <tr key={c.id} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-100 text-xs font-medium text-orange-800">
                            {c.customer_name.charAt(0)}
                          </div>
                          <div>
                            <p className="font-medium text-stone-900">{c.customer_name}</p>
                            <p className="text-xs text-stone-400">{c.customer_phone}</p>
                          </div>
                        </div>
                      </td>
                      <td className="hidden sm:table-cell px-4 py-3 text-right tabular-nums text-stone-700">
                        {c.item_count}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-stone-800">
                        {formatFCFA(c.total_value_fcfa)}
                      </td>
                      <td className="hidden md:table-cell px-4 py-3 text-stone-500">
                        {new Date(c.abandoned_at).toLocaleDateString("fr-SN")}
                      </td>
                      <td className="hidden lg:table-cell px-4 py-3 text-stone-500">
                        {c.reminder_sent_at
                          ? new Date(c.reminder_sent_at).toLocaleDateString("fr-SN")
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[c.status]}`}>
                          {STATUS_LABELS[c.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.total > pagination.limit && (
          <div className="flex items-center justify-between text-sm text-stone-500">
            <span>
              {((pagination.page - 1) * pagination.limit) + 1}–
              {Math.min(pagination.page * pagination.limit, pagination.total)} sur {pagination.total}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={pagination.page * pagination.limit >= pagination.total}
                className="rounded-md border border-stone-200 px-3 py-1.5 text-xs font-medium hover:bg-stone-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
